import React from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { Add, Close, ContentCopyTwoTone, Delete, Edit, Save } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { deleteItems, updateStateData } from "../../../../Store/actions";
import {
  fetchStoreSchedules,
  fetchSpecialHours,
  fetchQuietHours,
  legacyHoursToSchedule,
  saveRegularSchedules,
  validateStoreOpeningHours,
  deleteSpecialHours,
  deleteQuietHours,
} from "../../../../api_calls/infrastructure";
import AddEditSpecialHoursDialog from "./AddEditSpecialHoursDialog";
import AddEditQuietHoursDialog from "./AddEditQuietHoursDialog";
import { nextLocalScheduleId, WEEKDAY_DISPLAY_ORDER, formatDateDMY, getSpecialHoursStatus, getQuietHoursStatus } from "../../../../api_calls/utils";
import ActionButton from "../../../../components/commonComponents/Buttons/ActionButton";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import Confirmation from "../../../../components/commonComponents/Confirmation";
import ListingDataTable from "../../../../components/commonComponents/TabularLists/ListingDataTable";
import {
  ListingCellCenter,
  ListingCellLeft,
} from "../../../../components/commonComponents/TabularLists/styles";
import {
  StyledNestedTabs,
  StyledSwitch,
  HtmlTooltip,
  HtmlTooltipDisabled,
} from "../../../../components/commonComponents/StyledComponents/styled";
import StoreOpeningHours from "../../ModalContent/WizardSteps/StoreOpeningHours";
import { checkUserPermissions } from "../../../../api_calls/userManagement";

const WEEKDAY_SHORT = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

const getScheduleStatus = (schedule, today) => {
  if (schedule.active === false) return "inactive";
  if (!schedule.no_end && schedule.validity_end && schedule.validity_end < today) return "expired";
  if (schedule.validity_start && schedule.validity_start > today) return "upcoming";
  return "active";
};

// Copy sits immediately before Edit, then Delete — same icon markup as other CE lists.
// Links are flex boxes so the icon is not lifted by the text baseline and stays
// centered with the row text (and level with the disabled icons, which have no link).
const ScheduleRowActions = ({ canEdit, canDelete, disabledTitle = "Not Available", onCopy, onEdit, onDelete }) => (
  <ListingCellCenter>
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, flexWrap: "nowrap", "& > a": { display: "inline-flex" } }}>
      {canEdit ? (
        <HtmlTooltip title="Edit">
          <a
            href="#editSchedule"
            aria-label="Edit"
            onClick={(e) => {
              e.preventDefault();
              onEdit();
            }}
          >
            <Edit sx={{ color: "#AF3241" }} />
          </a>
        </HtmlTooltip>
      ) : (
        <HtmlTooltipDisabled title={disabledTitle}>
          <Edit sx={{ color: "#E6E6E6" }} />
        </HtmlTooltipDisabled>
      )}
      <HtmlTooltip title="Copy">
        <a
          href="#copySchedule"
          aria-label="Copy"
          onClick={(e) => {
            e.preventDefault();
            onCopy();
          }}
        >
          <ContentCopyTwoTone sx={{ color: "#AF3241" }} />
        </a>
      </HtmlTooltip>
      {canDelete ? (
        <HtmlTooltip title="Delete">
          <a
            href="#deleteSchedule"
            aria-label="Delete"
            onClick={(e) => {
              e.preventDefault();
              onDelete();
            }}
          >
            <Delete sx={{ color: "#AF3241" }} />
          </a>
        </HtmlTooltip>
      ) : (
        <HtmlTooltipDisabled title={disabledTitle}>
          <Delete sx={{ color: "#E6E6E6" }} />
        </HtmlTooltipDisabled>
      )}
    </Box>
  </ListingCellCenter>
);

const groupScheduleWeekdays = (schedule) => {
  const rows = [];
  let groupStart = 0;

  const timeDisplay = (day) => {
    const wd = schedule.weekdays[day];
    if (!wd || !wd.open) return ["Closed"];
    if (schedule.same_times_all_days) {
      if (schedule.same_times_is_24h) return ["Open 24 Hours"];
      const ranges = [...(schedule.same_times_ranges || [])].sort((a, b) => (a.open > b.open ? 1 : -1)).map((r) => r.open + " – " + r.close);
      return ranges.length > 0 ? ranges : ["Open"];
    }
    if (wd.is_24h) return ["Open 24 Hours"];
    return [...wd.time_ranges].sort((a, b) => (a.open > b.open ? 1 : -1)).map((r) => r.open + " – " + r.close);
  };

  const isSameDisplay = (a, b) => JSON.stringify(timeDisplay(a)) === JSON.stringify(timeDisplay(b));

  for (let i = 1; i <= WEEKDAY_DISPLAY_ORDER.length; i++) {
    if (i === WEEKDAY_DISPLAY_ORDER.length || !isSameDisplay(WEEKDAY_DISPLAY_ORDER[groupStart], WEEKDAY_DISPLAY_ORDER[i])) {
      const startDay = WEEKDAY_DISPLAY_ORDER[groupStart];
      const endDay = WEEKDAY_DISPLAY_ORDER[i - 1];
      const dayLabel =
        groupStart === i - 1
          ? WEEKDAY_SHORT[startDay]
          : WEEKDAY_SHORT[startDay] + " – " + WEEKDAY_SHORT[endDay];
      rows.push({ label: dayLabel, ranges: timeDisplay(startDay) });
      groupStart = i;
    }
  }
  return rows;
};

// Collapse UI state is ignored for discard confirmation.
const scheduleFingerprint = (schedules) =>
  JSON.stringify(schedules.map(({ is_collapsed, ...rest }) => rest));

const ConfigureRegularHoursPopup = ({ open, storeId, onClose, schedulesReady }) => {
  const userStateDetails = useSelector((state) => state.userSelections);
  const dispatchAction = useDispatch();
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);

  // Track the schedules as they were when the popup opened for dirty detection
  const originalSchedulesRef = React.useRef(null);
  const hydratedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      hydratedRef.current = false;
      return;
    }
    // Only hydrate after a successful fetch for this store (schedulesReady).
    if (!schedulesReady) return;
    if (hydratedRef.current) return;
    hydratedRef.current = true;

    const existing = userStateDetails.new_store_loc_data ?? {};
    let schedules = Array.isArray(userStateDetails.store_regular_schedules)
      ? userStateDetails.store_regular_schedules
      : [];

    if (schedules.length === 0) {
      const legacy = userStateDetails?.store_overview?.opening_hours;
      const converted = legacyHoursToSchedule(legacy, () => nextLocalScheduleId());
      schedules = converted ? [converted] : [];
    }

    // Sort time ranges within each schedule and same_times_ranges by open time on load
    const sortedRanges = (ranges, key = "open") =>
      [...ranges].sort((a, b) => (a[key] > b[key] ? 1 : -1));

    const normalizedSchedules = schedules.map((s) => ({
      ...s,
      // Start collapsed so multi-schedule lists stay scannable.
      is_collapsed: true,
      same_times_ranges: sortedRanges(s.same_times_ranges || []),
      weekdays: Object.fromEntries(
        Object.entries(s.weekdays).map(([day, data]) => [
          day,
          { ...data, time_ranges: sortedRanges(data.time_ranges || []) },
        ])
      ),
    })).sort((a, b) => {
      // Sort schedule boxes by start date once on load. While editing, cards
      // keep their order — live re-sorting on date changes is confusing.
      if (!a.validity_start) return 1;
      if (!b.validity_start) return -1;
      return a.validity_start > b.validity_start ? 1 : -1;
    });

    originalSchedulesRef.current = scheduleFingerprint(normalizedSchedules);
    dispatchAction(
      updateStateData({
        new_store_loc_data: { ...existing, opening_schedules: normalizedSchedules },
      })
    );
  }, [open, schedulesReady]);

  const isDirty = () => {
    const current = userStateDetails.new_store_loc_data?.opening_schedules ?? [];
    return scheduleFingerprint(current) !== originalSchedulesRef.current;
  };

  const handleSave = async () => {
    const validation = validateStoreOpeningHours();
    if (validation.error === true) {
      setFieldErrors(validation.errorMessages);
      return;
    }
    setFieldErrors({});

    const schedules = userStateDetails.new_store_loc_data?.opening_schedules ?? [];
    const result = await saveRegularSchedules(storeId, schedules);
    if (result.success === true) {
      dispatchAction(deleteItems(["new_store_loc_data"]));
      onClose();
    }
  };

  const handleCancel = () => {
    if (isDirty()) {
      setShowCancelConfirmation(true);
    } else {
      dispatchAction(deleteItems(["new_store_loc_data"]));
      onClose();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(e) => e.preventDefault()}
        fullWidth={false}
        maxWidth={false}
        sx={{ "& .MuiDialog-paper": { width: "1320px", height: "850px", maxWidth: "none" } }}
      >
        <DialogTitle>Configure Regular Opening Hours</DialogTitle>

        <DialogContent sx={{ overflowY: "auto" }}>
          <StoreOpeningHours fieldErrors={fieldErrors} usedFor="update" />
        </DialogContent>

        <Box height={20} />

        <DialogActions>
          <Stack direction="row" justifyContent="space-between" width="100%">
            <CloseButton
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
              label="Cancel"
              icon={<Close />}
            />
            <ActionButton
              onClick={(e) => {
                e.preventDefault();
                handleSave();
              }}
              label="Save"
              icon={<Save />}
              sx={{ mt: 0, mb: 0 }}
            />
          </Stack>
        </DialogActions>
      </Dialog>

      {showCancelConfirmation && (
        <Confirmation
          showConfirmationPopup={showCancelConfirmation}
          closePopup={() => setShowCancelConfirmation(false)}
          confirmationActionCall={() => {
            dispatchAction(deleteItems(["new_store_loc_data"]));
            onClose();
          }}
          confirmationMessage="Are you certain you want to discard all the changes you have made?"
          confirmationTitle="Confirmation"
        />
      )}
    </>
  );
};

const RegularHoursSection = ({ storeId, showEditButton, schedulesReady }) => {
  const userStateData = useSelector((state) => state.userSelections);
  const [showExpired, setShowExpired] = React.useState(false);
  const [isConfigureOpen, setIsConfigureOpen] = React.useState(false);

  const today = new Date().toISOString().slice(0, 10);

  // Until this store's fetch succeeds, do not render another store's Redux data.
  const schedules =
    schedulesReady && Array.isArray(userStateData.store_regular_schedules)
      ? userStateData.store_regular_schedules
      : [];

  const visibleSchedules = (showExpired ? [...schedules] : schedules.filter((s) => {
    if (s.no_end) return true;
    if (!s.validity_end) return true;
    return s.validity_end >= today;
  })).sort((a, b) => {
    if (!a.validity_start) return 1;
    if (!b.validity_start) return -1;
    return a.validity_start > b.validity_start ? 1 : -1;
  });

  return (
    <>
      <Typography variant="body2" sx={{ color: "#62626E", mb: 4 }}>
        Define all regular opening hour schedules for the store. These times are valid if no Special Hours are applied.
      </Typography>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {showEditButton ? (
          <ActionButton
            label="Configure Regular Hours"
            icon={<Edit />}
            onClick={() => {
              if (!schedulesReady) return;
              setIsConfigureOpen(true);
            }}
            sx={{ mt: 0, mb: 0 }}
          />
        ) : (
          <Box />
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledSwitch
            checked={showExpired}
            size="small"
            onChange={(e) => setShowExpired(e.target.checked)}
          />
          <Typography variant="body2">Show Expired</Typography>
        </Stack>
      </Stack>

      {visibleSchedules.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#62626E" }}>
          No schedules configured yet.
        </Typography>
      ) : (
        visibleSchedules.map((schedule) => {
          const status = getScheduleStatus(schedule, today);
          const groups = groupScheduleWeekdays(schedule);
          const startLabel = schedule.validity_start ? formatDateDMY(schedule.validity_start) : "—";
          const endLabel = schedule.no_end ? "Indefinitely" : schedule.validity_end ? formatDateDMY(schedule.validity_end) : "—";

          return (
            <Paper
              key={schedule.id}
              variant="outlined"
              sx={{ mb: 2, borderRadius: 1, p: 2 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                {schedule.name || "Unnamed"}{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{ color: "#62626E", textTransform: "capitalize" }}
                >
                  ({status})
                </Typography>
              </Typography>
              <Typography variant="caption" sx={{ color: "#62626E", display: "block", mb: 1.5 }}>
                Validity: {startLabel} – {endLabel}
              </Typography>

              {groups.map((row, idx) => (
                <Stack key={idx} direction="row" spacing={3} alignItems="flex-start" sx={{ mb: 0.25 }}>
                  <Typography variant="body2" sx={{ minWidth: 80 }}>
                    <strong>{row.label}:</strong>
                  </Typography>
                  <Stack direction="column" spacing={0}>
                    {row.ranges.map((t, i) => (
                      <Typography key={i} variant="body2">{t}</Typography>
                    ))}
                  </Stack>
                </Stack>
              ))}
            </Paper>
          );
        })
      )}

      {isConfigureOpen && (
        <ConfigureRegularHoursPopup
          open={isConfigureOpen}
          storeId={storeId}
          schedulesReady={schedulesReady}
          onClose={() => setIsConfigureOpen(false)}
        />
      )}
    </>
  );
};

const SpecialHoursSection = ({ storeId, showEditButton }) => {
  const userStateData = useSelector((state) => state.userSelections);
  const [showExpired, setShowExpired] = React.useState(false);
  const [dialogState, setDialogState] = React.useState({ open: false, mode: "add", entry: null });
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const allEntries = Array.isArray(userStateData.store_special_hours)
    ? userStateData.store_special_hours
    : [];

  const existingDates = allEntries.map((e) => e.valid_on).filter(Boolean);

  const visibleEntries = (showExpired
    ? [...allEntries]
    : allEntries.filter((e) => (e.valid_on || "") >= today)
  ).sort((a, b) => ((a.valid_on || "") > (b.valid_on || "") ? 1 : -1));

  const handleAdd = () => setDialogState({ open: true, mode: "add", entry: null });
  const handleEdit = (entry) => setDialogState({ open: true, mode: "edit", entry });
  const handleCopy = (entry) =>
    setDialogState({
      open: true,
      mode: "add",
      entry: { ...entry, id: null, valid_on: null },
    });
  const handleDialogClose = () => setDialogState((prev) => ({ ...prev, open: false }));
  const handleSaved = () => setDialogState((prev) => ({ ...prev, open: false }));

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteSpecialHours(storeId, deleteTarget.id);
    setDeleteTarget(null);
  };

  const specialHoursColumns = React.useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        render: (row) => <ListingCellLeft>{row.name}</ListingCellLeft>,
      },
      {
        id: "date",
        header: "Date",
        render: (row) => <ListingCellLeft>{formatDateDMY(row.valid_on)}</ListingCellLeft>,
      },
      {
        id: "open_hours",
        header: "Open Hours",
        render: (row) => (
          <ListingCellLeft>
            <Box>
              {row.type === "Open 24 Hours"
                ? "Open 24 Hours"
                : row.type === "Closed"
                  ? "Closed"
                  : (row.open_hours || []).map((r, i) => (
                    <div key={i}>
                      {r.open} – {r.close}
                    </div>
                  ))}
            </Box>
          </ListingCellLeft>
        ),
      },
      {
        id: "quiet_hours",
        header: "Quiet Hours",
        render: (row) => (
          <ListingCellLeft>
            <Box>
              {!row.quiet_hours || row.quiet_hours.length === 0
                ? "No"
                : row.quiet_hours.map((r, i) => (
                  <div key={i}>
                    {r.start} – {r.end}
                  </div>
                ))}
            </Box>
          </ListingCellLeft>
        ),
      },
      {
        id: "action",
        header: "Action",
        headerAlign: "center",
        align: "center",
        hidden: !showEditButton,
        render: (row) => {
          const status = getSpecialHoursStatus(row, today);
          const canMutate = status === "scheduled";
          return (
            <ScheduleRowActions
              canEdit={canMutate}
              canDelete={canMutate}
              disabledTitle={status === "expired" ? "Not available after the schedule has expired" : "Not available while the schedule is active"}
              onCopy={() => handleCopy(row)}
              onEdit={() => handleEdit(row)}
              onDelete={() => setDeleteTarget(row)}
            />
          );
        },
      },
    ],
    [showEditButton, today]
  );

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ color: "#62626E", mb: 0.2 }}>
          Each date can have only one special schedule.
        </Typography>
        <Typography variant="body2" sx={{ color: "#62626E", mb: 0.2 }}>
          Special Hours replace the regular opening hours for that day.
        </Typography>
        <Typography variant="body2" sx={{ color: "#62626E" }}>
          Only Quiet Hours defined in the special schedule apply. Regular Quiet Hours are ignored.
        </Typography>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {showEditButton ? (
          <ActionButton label="Add Special Hours" icon={<Add />} onClick={handleAdd} sx={{ mt: 0, mb: 0 }} />
        ) : (
          <Box />
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledSwitch
            checked={showExpired}
            size="small"
            onChange={(e) => setShowExpired(e.target.checked)}
          />
          <Typography variant="body2">Show Expired</Typography>
        </Stack>
      </Stack>

      <ListingDataTable
        columns={specialHoursColumns}
        rows={visibleEntries}
        rowKey={(row) => row.id}
        emptyText="No Special Hours configured."
        pagination={null}
        onPageChange={null}
        onPageSizeChange={null}
      />

      <AddEditSpecialHoursDialog
        open={dialogState.open}
        mode={dialogState.mode}
        entry={dialogState.entry}
        storeId={storeId}
        existingDates={existingDates}
        onClose={handleDialogClose}
        onSaved={handleSaved}
      />

      {deleteTarget && (
        <Confirmation
          showConfirmationPopup={!!deleteTarget}
          closePopup={() => setDeleteTarget(null)}
          confirmationActionCall={handleDeleteConfirm}
          confirmationMessage="Are you certain you want to delete these Special Hours?"
          confirmationTitle="Delete Special Hours"
        />
      )}
    </>
  );
};

const validityDisplay = (entry) => {
  if (entry.type === "once") return entry.valid_on ? formatDateDMY(entry.valid_on) : "—";
  const start = entry.validity_start ? formatDateDMY(entry.validity_start) : "—";
  const end = entry.no_end ? "Indefinitely" : entry.validity_end ? formatDateDMY(entry.validity_end) : "—";
  return start + " – " + end;
};

// Groups consecutive weekdays into ranges for compact display, e.g. Mon – Thu, Sat
const groupWeekdayList = (days) => {
  if (days.length === 0) return "—";
  const indices = days
    .map((d) => WEEKDAY_DISPLAY_ORDER.indexOf(d))
    .filter((i) => i !== -1)
    .sort((a, b) => a - b);
  const parts = [];
  let rangeStart = indices[0];
  let rangeEnd = indices[0];
  for (let i = 1; i <= indices.length; i++) {
    if (i < indices.length && indices[i] === rangeEnd + 1) {
      rangeEnd = indices[i];
    } else {
      parts.push(
        rangeStart === rangeEnd
          ? WEEKDAY_SHORT[WEEKDAY_DISPLAY_ORDER[rangeStart]]
          : WEEKDAY_SHORT[WEEKDAY_DISPLAY_ORDER[rangeStart]] + " – " + WEEKDAY_SHORT[WEEKDAY_DISPLAY_ORDER[rangeEnd]]
      );
      if (i < indices.length) {
        rangeStart = indices[i];
        rangeEnd = indices[i];
      }
    }
  }
  return parts.join(", ");
};

const repeatsOnDisplay = (entry) => {
  if (entry.type === "once") return "Never";
  const days = entry.repeat_on || [];
  return groupWeekdayList(days);
};

const nextActiveDisplay = (entry) => {
  if (!entry.next_occurrence) return "—";
  return formatDateDMY(entry.next_occurrence);
};

const QuietHoursSection = ({ storeId, showEditButton }) => {
  const userStateData = useSelector((state) => state.userSelections);
  const [showExpired, setShowExpired] = React.useState(false);
  const [dialogState, setDialogState] = React.useState({ open: false, mode: "add", entry: null });
  const [deleteTarget, setDeleteTarget] = React.useState(null);

  const today = new Date().toISOString().slice(0, 10);

  const allEntries = Array.isArray(userStateData.store_quiet_hours)
    ? userStateData.store_quiet_hours
    : [];

  const isExpiredEntry = (entry) => {
    if (entry.type === "once") return (entry.valid_on || "") < today;
    if (entry.type === "weekly") {
      if (entry.no_end) return false;
      if (!entry.validity_end) return false;
      return entry.validity_end < today;
    }
    return false;
  };

  const visibleEntries = (showExpired
    ? [...allEntries]
    : allEntries.filter((e) => !isExpiredEntry(e))
  ).sort((a, b) => {
    const dateA = a.validity_start || a.valid_on || "";
    const dateB = b.validity_start || b.valid_on || "";
    return dateA > dateB ? 1 : -1;
  });

  const handleAdd = () => setDialogState({ open: true, mode: "add", entry: null });
  const handleEdit = (entry) => setDialogState({ open: true, mode: "edit", entry });
  const handleCopy = (entry) =>
    setDialogState({
      open: true,
      mode: "add",
      entry: {
        ...entry,
        id: null,
        validity_start: null,
        validity_end: null,
        no_end: false,
        valid_on: null,
      },
    });
  const handleDialogClose = () => setDialogState((prev) => ({ ...prev, open: false }));
  const handleSaved = () => setDialogState((prev) => ({ ...prev, open: false }));

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteQuietHours(storeId, deleteTarget.id);
    setDeleteTarget(null);
  };

  const quietHoursColumns = React.useMemo(
    () => [
      {
        id: "validity",
        header: "Validity",
        render: (row) => <ListingCellLeft>{validityDisplay(row)}</ListingCellLeft>,
      },
      {
        id: "repeats_on",
        header: "Repeats on",
        render: (row) => <ListingCellLeft>{repeatsOnDisplay(row)}</ListingCellLeft>,
      },
      {
        id: "quiet_hours",
        header: "Quiet Hours",
        render: (row) => (
          <ListingCellLeft>
            <Box>
              {(row.time_ranges || []).map((r, i) => (
                <div key={i}>
                  {r.start} – {r.end}
                </div>
              ))}
            </Box>
          </ListingCellLeft>
        ),
      },
      {
        id: "next_active",
        header: "Next active on",
        render: (row) => <ListingCellLeft>{nextActiveDisplay(row)}</ListingCellLeft>,
      },
      {
        id: "action",
        header: "Action",
        headerAlign: "center",
        align: "center",
        hidden: !showEditButton,
        render: (row) => {
          const status = getQuietHoursStatus(row, today);
          return (
            <ScheduleRowActions
              canEdit={status !== "expired"}
              canDelete={status === "scheduled"}
              disabledTitle={status === "expired" ? "Not available after the schedule has expired" : "Not available while the schedule is active"}
              onCopy={() => handleCopy(row)}
              onEdit={() => handleEdit(row)}
              onDelete={() => setDeleteTarget(row)}
            />
          );
        },
      },
    ],
    [showEditButton, today]
  );

  return (
    <>
      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ color: "#62626E", mb: 0.2 }}>
          During Quiet Hours, advertising is displayed without sound.
        </Typography>
        <Typography variant="body2" sx={{ color: "#62626E" }}>
          Quiet Hours outside Regular Opening Hours are ignored.
        </Typography>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        {showEditButton ? (
          <ActionButton label="Add Quiet Hours" icon={<Add />} onClick={handleAdd} sx={{ mt: 0, mb: 0 }} />
        ) : (
          <Box />
        )}

        <Stack direction="row" alignItems="center" spacing={1}>
          <StyledSwitch
            checked={showExpired}
            size="small"
            onChange={(e) => setShowExpired(e.target.checked)}
          />
          <Typography variant="body2">Show Expired</Typography>
        </Stack>
      </Stack>

      <ListingDataTable
        columns={quietHoursColumns}
        rows={visibleEntries}
        rowKey={(row) => row.id}
        emptyText="No Quiet Hours configured."
        pagination={null}
        onPageChange={null}
        onPageSizeChange={null}
      />

      <AddEditQuietHoursDialog
        open={dialogState.open}
        mode={dialogState.mode}
        entry={dialogState.entry}
        storeId={storeId}
        existingEntries={allEntries}
        onClose={handleDialogClose}
        onSaved={handleSaved}
      />

      {deleteTarget && (
        <Confirmation
          showConfirmationPopup={!!deleteTarget}
          closePopup={() => setDeleteTarget(null)}
          confirmationActionCall={handleDeleteConfirm}
          confirmationMessage="Are you certain you want to delete these Quiet Hours?"
          confirmationTitle="Delete Quiet Hours"
        />
      )}
    </>
  );
};

const SUB_SECTIONS = ["Regular Hours", "Special Hours", "Quiet Hours"];

const OpeningHoursTab = () => {
  const [activeSection, setActiveSection] = React.useState(0);
  // Set only after a successful fetch for the current storeId — never clear Redux.
  const [schedulesStoreId, setSchedulesStoreId] = React.useState(null);
  const { id: storeId } = useParams();
  const schedulesReady = schedulesStoreId === storeId;

  const allowedAccessRights = checkUserPermissions();
  const permissionScope = allowedAccessRights.allowed_access;

  const showEditButton = permissionScope.infra_store_list_can_edit === true;

  React.useEffect(() => {
    let alive = true;
    setSchedulesStoreId(null);
    fetchStoreSchedules(storeId).then((result) => {
      if (alive && result?.success === true) {
        setSchedulesStoreId(storeId);
      }
    });
    fetchSpecialHours(storeId);
    fetchQuietHours(storeId);
    return () => {
      alive = false;
    };
  }, [storeId]);

  const renderSection = () => {
    switch (activeSection) {
      case 0:
        return (
          <RegularHoursSection
            storeId={storeId}
            showEditButton={showEditButton}
            schedulesReady={schedulesReady}
          />
        );
      case 1:
        return <SpecialHoursSection storeId={storeId} showEditButton={showEditButton} />;
      case 2:
        return <QuietHoursSection storeId={storeId} showEditButton={showEditButton} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ pt: 2 }}>
      <StyledNestedTabs
        value={activeSection}
        onChange={(_, newValue) => setActiveSection(newValue)}
        textColor={"#F04B55"}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        {SUB_SECTIONS.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </StyledNestedTabs>

      {renderSection()}
    </Box>
  );
};

export default OpeningHoursTab;
